/**
 * Offline Sync Utilities
 * - Queue read/star actions when offline
 * - Sync to backend when online
 * - Use IndexedDB for persistent storage
 * - Integrate with Background Sync API
 */

interface PendingAction {
  id: string;
  type: 'read' | 'star' | 'unstar';
  articleId: string;
  timestamp: number;
  data?: any;
}

interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors?: string[];
}

const DB_NAME = 'plotlake-sync';
const DB_VERSION = 1;
const STORES = {
  PENDING_READS: 'pending-reads',
  PENDING_STARS: 'pending-stars',
  PENDING_ACTIONS: 'pending-actions',
  CACHED_ARTICLES: 'cached-articles',
};

/**
 * Open IndexedDB connection
 */
export function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create object stores if they don't exist
      if (!db.objectStoreNames.contains(STORES.PENDING_READS)) {
        db.createObjectStore(STORES.PENDING_READS, { autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORES.PENDING_STARS)) {
        db.createObjectStore(STORES.PENDING_STARS, { autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORES.PENDING_ACTIONS)) {
        db.createObjectStore(STORES.PENDING_ACTIONS, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORES.CACHED_ARTICLES)) {
        db.createObjectStore(STORES.CACHED_ARTICLES, { keyPath: 'id' });
      }
    };
  });
}

/**
 * Add item to store
 */
function addToStore<T>(db: IDBDatabase, storeName: string, item: T): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.add(item);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

/**
 * Get all items from store
 */
function getAllFromStore<T>(db: IDBDatabase, storeName: string): Promise<T[]> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result as T[]);
  });
}

/**
 * Get single item from store
 */
function getFromStore<T>(db: IDBDatabase, storeName: string, key: string): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(key);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result as T | undefined);
  });
}

/**
 * Put item in store (upsert)
 */
function putInStore<T>(db: IDBDatabase, storeName: string, item: T): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(item);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

/**
 * Delete item from store
 */
function deleteFromStore(db: IDBDatabase, storeName: string, key: IDBValidKey): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(key);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

/**
 * Clear entire store
 */
function clearStore(db: IDBDatabase, storeName: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

/**
 * Queue a read action (offline-capable)
 */
export async function queueReadAction(articleId: string): Promise<void> {
  try {
    const db = await openDB();
    const action: PendingAction = {
      id: `read-${articleId}-${Date.now()}`,
      type: 'read',
      articleId,
      timestamp: Date.now(),
    };

    await addToStore(db, STORES.PENDING_READS, action);
    console.log('[OfflineSync] Read action queued:', articleId);

    // Trigger background sync if available
    if ('serviceWorker' in navigator && 'sync' in (ServiceWorkerRegistration.prototype as any)) {
      const registration = await navigator.serviceWorker.ready;
      await (registration as any).sync.register('sync-read-states');
      console.log('[OfflineSync] Background sync registered for reads');
    }
  } catch (error) {
    console.error('[OfflineSync] Failed to queue read action:', error);
    throw error;
  }
}

/**
 * Queue a star action (offline-capable)
 */
export async function queueStarAction(articleId: string, starred: boolean): Promise<void> {
  try {
    const db = await openDB();
    const action: PendingAction = {
      id: `star-${articleId}-${Date.now()}`,
      type: starred ? 'star' : 'unstar',
      articleId,
      timestamp: Date.now(),
      data: { starred },
    };

    await addToStore(db, STORES.PENDING_STARS, action);
    console.log('[OfflineSync] Star action queued:', articleId, starred);

    // Trigger background sync if available
    if ('serviceWorker' in navigator && 'sync' in (ServiceWorkerRegistration.prototype as any)) {
      const registration = await navigator.serviceWorker.ready;
      await (registration as any).sync.register('sync-star-states');
      console.log('[OfflineSync] Background sync registered for stars');
    }
  } catch (error) {
    console.error('[OfflineSync] Failed to queue star action:', error);
    throw error;
  }
}

/**
 * Manually sync pending actions (when online)
 */
export async function syncPendingActions(): Promise<SyncResult> {
  const result: SyncResult = {
    success: false,
    synced: 0,
    failed: 0,
    errors: [],
  };

  try {
    const db = await openDB();

    // Sync read actions
    const pendingReads = await getAllFromStore<PendingAction>(db, STORES.PENDING_READS);
    console.log('[OfflineSync] Syncing', pendingReads.length, 'read actions');

    for (const action of pendingReads) {
      try {
        const response = await fetch('/api/articles/mark-read', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ article_id: action.articleId }),
        });

        if (response.ok) {
          await deleteFromStore(db, STORES.PENDING_READS, action.id);
          result.synced++;
        } else {
          result.failed++;
          result.errors?.push(`Read action failed: ${action.articleId}`);
        }
      } catch (error) {
        result.failed++;
        result.errors?.push(`Read action error: ${error}`);
      }
    }

    // Sync star actions
    const pendingStars = await getAllFromStore<PendingAction>(db, STORES.PENDING_STARS);
    console.log('[OfflineSync] Syncing', pendingStars.length, 'star actions');

    for (const action of pendingStars) {
      try {
        const endpoint = action.type === 'star' ? '/api/articles/star' : '/api/articles/unstar';
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ article_id: action.articleId }),
        });

        if (response.ok) {
          await deleteFromStore(db, STORES.PENDING_STARS, action.id);
          result.synced++;
        } else {
          result.failed++;
          result.errors?.push(`Star action failed: ${action.articleId}`);
        }
      } catch (error) {
        result.failed++;
        result.errors?.push(`Star action error: ${error}`);
      }
    }

    result.success = result.failed === 0;
    console.log('[OfflineSync] Sync complete:', result);
    return result;
  } catch (error) {
    console.error('[OfflineSync] Sync failed:', error);
    result.errors?.push(`Sync error: ${error}`);
    return result;
  }
}

/**
 * Get count of pending actions
 */
export async function getPendingCount(): Promise<{ reads: number; stars: number; total: number }> {
  try {
    const db = await openDB();
    const pendingReads = await getAllFromStore(db, STORES.PENDING_READS);
    const pendingStars = await getAllFromStore(db, STORES.PENDING_STARS);

    return {
      reads: pendingReads.length,
      stars: pendingStars.length,
      total: pendingReads.length + pendingStars.length,
    };
  } catch (error) {
    console.error('[OfflineSync] Failed to get pending count:', error);
    return { reads: 0, stars: 0, total: 0 };
  }
}

/**
 * Cache article for offline reading
 */
export async function cacheArticle(article: any): Promise<void> {
  try {
    const db = await openDB();
    await putInStore(db, STORES.CACHED_ARTICLES, {
      id: article.id,
      ...article,
      cachedAt: Date.now(),
    });

    // Also tell service worker to cache the article
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CACHE_ARTICLE',
        article,
      });
    }

    console.log('[OfflineSync] Article cached:', article.id);
  } catch (error) {
    console.error('[OfflineSync] Failed to cache article:', error);
    throw error;
  }
}

/**
 * Get cached article
 */
export async function getCachedArticle(articleId: string): Promise<any | null> {
  try {
    const db = await openDB();
    const article = await getFromStore(db, STORES.CACHED_ARTICLES, articleId);
    return article || null;
  } catch (error) {
    console.error('[OfflineSync] Failed to get cached article:', error);
    return null;
  }
}

/**
 * Get all cached articles
 */
export async function getAllCachedArticles(): Promise<any[]> {
  try {
    const db = await openDB();
    return await getAllFromStore(db, STORES.CACHED_ARTICLES);
  } catch (error) {
    console.error('[OfflineSync] Failed to get cached articles:', error);
    return [];
  }
}

/**
 * Clear old cached articles (older than 7 days)
 */
export async function clearOldCachedArticles(
  maxAge: number = 7 * 24 * 60 * 60 * 1000,
): Promise<number> {
  try {
    const db = await openDB();
    const articles = await getAllFromStore<any>(db, STORES.CACHED_ARTICLES);
    const now = Date.now();
    let deleted = 0;

    for (const article of articles) {
      if (article.cachedAt && now - article.cachedAt > maxAge) {
        await deleteFromStore(db, STORES.CACHED_ARTICLES, article.id);
        deleted++;
      }
    }

    console.log('[OfflineSync] Cleared', deleted, 'old cached articles');
    return deleted;
  } catch (error) {
    console.error('[OfflineSync] Failed to clear old cached articles:', error);
    return 0;
  }
}

/**
 * Get last sync time from localStorage
 */
export function getLastSyncTime(): number | null {
  const lastSync = localStorage.getItem('lastSyncTime');
  return lastSync ? parseInt(lastSync, 10) : null;
}

/**
 * Set last sync time in localStorage
 */
export function setLastSyncTime(timestamp: number): void {
  localStorage.setItem('lastSyncTime', timestamp.toString());
}

/**
 * Auto-sync when online
 */
export function setupAutoSync(): void {
  if (typeof window === 'undefined') return;

  // Sync when coming back online
  window.addEventListener('online', async () => {
    console.log('[OfflineSync] Back online, syncing...');
    const result = await syncPendingActions();
    if (result.success) {
      setLastSyncTime(Date.now());
    }
  });

  // Periodic sync check (every 5 minutes if online)
  setInterval(
    async () => {
      if (navigator.onLine) {
        const pendingCount = await getPendingCount();
        if (pendingCount.total > 0) {
          console.log('[OfflineSync] Periodic sync check, pending:', pendingCount.total);
          await syncPendingActions();
        }
      }
    },
    5 * 60 * 1000,
  );
}
